import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useTranslation } from '@/i18n';
import { APP_NAME } from '@/config/env';
import styles from './StyleGuidePage.module.css';

/**
 * The living style guide.
 *
 * It exists so a token change is seen immediately and in every context, rather
 * than discovered three screens later — and so the next person can find out what
 * already exists before building a fourth button. It is a real route, not a
 * Storybook: one fewer tool to install, and it renders in the same bundle as the
 * app, which means it cannot drift from it.
 */

const SCALES = ['neutral', 'primary', 'success', 'warning', 'danger', 'accentAlt'] as const;
const STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950, 975] as const;

const SURFACE_ROLES = ['surface-page', 'surface-card', 'surface-sunken', 'surface-hover', 'surface-selected', 'accent-muted'] as const;
const INK_ROLES = ['ink-primary', 'ink-secondary', 'ink-muted', 'accent-ink'] as const;
const LINE_ROLES = ['border', 'border-strong', 'divider'] as const;
const STATUS_ROLES = ['accent', 'success', 'warning', 'danger', 'info'] as const;

const TYPE_SPECIMEN = [
  { token: '--text-3xl', label: 'text-3xl / 30px', weight: 600 },
  { token: '--text-2xl', label: 'text-2xl / 24px', weight: 600 },
  { token: '--text-xl', label: 'text-xl / 20px', weight: 600 },
  { token: '--text-lg', label: 'text-lg / 16px', weight: 500 },
  { token: '--text-md', label: 'text-md / 14px', weight: 400 },
  { token: '--text-sm', label: 'text-sm / 13px', weight: 400 },
  { token: '--text-xs', label: 'text-xs / 12px', weight: 400 },
  { token: '--text-2xs', label: 'text-2xs / 11px', weight: 500 },
] as const;

const FLIGHTS = [
  { flight: 'KC 931', route: 'NQZ → ALA', scheduled: '12:40', estimated: '13:10', status: 'Delayed', tone: 'warning' },
  { flight: 'KC 104', route: 'ALA → NQZ', scheduled: '09:05', estimated: '09:05', status: 'Departed', tone: 'success' },
  { flight: 'FZ 1722', route: 'DXB → NQZ', scheduled: '04:15', estimated: '—', status: 'Cancelled', tone: 'danger' },
  { flight: 'TK 358', route: 'IST → NQZ', scheduled: '18:30', estimated: '18:45', status: 'Expected', tone: 'info' },
] as const;

const Section = ({
  title,
  note,
  children,
}: {
  title: string;
  note: string;
  children: ReactNode;
}) => (
  <section className={styles.section}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    <p className={styles.sectionNote}>{note}</p>
    {children}
  </section>
);

export const StyleGuidePage = () => {
  const { t, locale, formatDateTime, formatDateLong, formatNumber } = useTranslation();

  // Plurals are why this section exists: 1 / 3 / 5 / 21 land on different forms
  // in Russian and on one form in Kazakh. Deciding by count === 1, which is what
  // hand-rolled i18n does, is wrong for every value above four.
  const counts = [1, 3, 5, 21] as const;

  return (
  <div className={styles.page}>
    <header className={styles.header}>
      <div>
        <h1 className={styles.title}>{APP_NAME} — design system</h1>
        <p className={styles.subtitle}>
          Tokens, type and primitives. Every colour pair here is contrast-checked by
          <code className={styles.mono}> npm run tokens:check</code>.
        </p>
      </div>
      <div className={styles.settings}>
        <LanguageSwitcher />
        <ThemeToggle showLabel />
      </div>
    </header>

    <Section
      title="Type"
      note="Inter for the interface, JetBrains Mono for raw messages and codes. Both self-hosted, so a proxy in front of this app can never drop the interface to a fallback face. The scale steps by 1.2 from a 14px base — dense enough for tables, with real distance between levels."
    >
      <div className={styles.card}>
        {TYPE_SPECIMEN.map(({ token, label, weight }) => (
          <div className={styles.specimen} key={token}>
            <span className={styles.specimenMeta}>{label}</span>
            <span style={{ fontSize: `var(${token})`, fontWeight: weight }}>
              Astana · Алматы · NQZ 12:40
            </span>
          </div>
        ))}
        <div className={styles.specimen}>
          <span className={styles.specimenMeta}>mono / tabular</span>
          <span className={`${styles.mono} tabular`}>ETD 12:40 · ETA 13:10 · 0O1lI8B</span>
        </div>
      </div>
    </Section>

    <Section
      title="Colour scales"
      note="Generated in OKLCH from one lightness ramp shared by every hue, so step 600 of the blue and step 600 of the red carry the same weight. Raw values with no meaning attached — components never reference these."
    >
      <div className={styles.stack}>
        {SCALES.map((scale) => (
          <div key={scale}>
            <div className={styles.scaleName}>{scale}</div>
            <div className={styles.scale}>
              {STEPS.map((step) => (
                <div
                  className={styles.scaleStep}
                  key={step}
                  style={{ backgroundColor: `var(--${scale}-${step})` }}
                  title={`${scale}-${step}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>

    <Section
      title="Roles"
      note="What components actually use. Named for the job — ink-secondary, not grey-600 — because a role can be re-pointed for the dark theme and a shade cannot. Switch the theme above and watch every one of these move."
    >
      <div className={styles.card}>
        <div className={styles.stack}>
          {[
            ['Surfaces', SURFACE_ROLES],
            ['Ink', INK_ROLES],
            ['Lines', LINE_ROLES],
            ['Status', STATUS_ROLES],
          ].map(([groupLabel, roles]) => (
            <div key={groupLabel as string}>
              <div className={styles.scaleName}>{groupLabel as string}</div>
              <ul className={styles.roleList}>
                {(roles as readonly string[]).map((role) => (
                  <li className={styles.role} key={role}>
                    <span className={styles.roleChip} style={{ backgroundColor: `var(--${role})` }} />
                    {role}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </Section>

    <Section
      title="Buttons"
      note="The variant states the job: primary is the one action a screen exists for, danger destroys something, ghost is for the controls that crowd a toolbar. Heights come from the shared control scale, so buttons and inputs in one row line up without nudging."
    >
      <div className={styles.card}>
        <div className={styles.stack}>
          <div className={styles.row}>
            <Button variant="primary">Save changes</Button>
            <Button variant="secondary">Cancel</Button>
            <Button variant="ghost">Reset filters</Button>
            <Button variant="danger">Delete airport</Button>
            <Button variant="primary" disabled>
              Saving…
            </Button>
          </div>
          <div className={styles.row}>
            <Button size="sm">Small</Button>
            <Button size="md">Medium</Button>
            <Button size="lg" variant="primary">
              Large
            </Button>
            <Button size="sm" iconOnly aria-label="Refresh">
              ⟳
            </Button>
          </div>
        </div>
      </div>
    </Section>

    <Section
      title="Badges"
      note="A tint plus a word. The tone is a hint and never the message — the text always says what the state is, so nothing is lost in greyscale, in print, or to a reader who cannot separate the hues."
    >
      <div className={styles.card}>
        <div className={styles.row}>
          <Badge>Archived</Badge>
          <Badge tone="accent">Updated</Badge>
          <Badge tone="success">On time</Badge>
          <Badge tone="warning">Delayed</Badge>
          <Badge tone="danger">Cancelled</Badge>
          <Badge tone="info">Expected</Badge>
          <Badge tone="accent" code>
            NQZ
          </Badge>
          <Badge code>KC 931</Badge>
        </div>
      </div>
    </Section>

    <Section
      title="Fields"
      note="Label, control and message in one component, with the id wired up here so a caller cannot forget it. An invalid field is stated by aria-invalid and by the message, not by a red border alone."
    >
      <div className={styles.card}>
        <div className={styles.grid}>
          <Field label="IATA code" placeholder="NQZ" required hint="Three letters." />
          <Field label="Airport name" defaultValue="Astana International" />
          <Field label="Country code" defaultValue="KZ" error="Unknown country code." />
          <Field label="Time zone" defaultValue="Asia/Almaty" disabled hint="Set by the backend." />
        </div>
      </div>
    </Section>

    <Section
      title="Table"
      note="The shape most of this product takes. Uppercase heads on a sunken well, hairline dividers, hover on the row, tabular figures so times align digit for digit, and codes monospaced."
    >
      <div className={styles.card}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Flight</th>
              <th>Route</th>
              <th>Scheduled</th>
              <th>Estimated</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {FLIGHTS.map((row) => (
              <tr key={row.flight}>
                <td className={styles.mono}>{row.flight}</td>
                <td className={styles.mono}>{row.route}</td>
                <td className="tabular">{row.scheduled}</td>
                <td className="tabular">{row.estimated}</td>
                <td>
                  <Badge tone={row.tone}>{row.status}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
    <Section
      title="Language"
      note="Kazakh, Russian and English, switched in the header. Keys are checked at compile time against the English dictionary, so a locale cannot fall behind silently. Plurals, dates and numbers all come from Intl for the active locale."
    >
      <div className={styles.card}>
        <dl className={styles.samples}>
          <div className={styles.sample}>
            <dt className={styles.sampleKey}>locale</dt>
            <dd className={styles.sampleValue}>{locale}</dd>
          </div>
          <div className={styles.sample}>
            <dt className={styles.sampleKey}>app.name</dt>
            <dd className={styles.sampleValue}>{t('app.name')} — {t('app.tagline')}</dd>
          </div>
          <div className={styles.sample}>
            <dt className={styles.sampleKey}>flight.found</dt>
            <dd className={styles.sampleValue}>{counts.map((count) => t('flight.found', { count })).join(' · ')}</dd>
          </div>
          <div className={styles.sample}>
            <dt className={styles.sampleKey}>flight.delayedBy</dt>
            <dd className={styles.sampleValue}>{t('flight.delayedBy', { count: 25 })}</dd>
          </div>
          <div className={styles.sample}>
            <dt className={styles.sampleKey}>demo.signedInAs</dt>
            <dd className={styles.sampleValue}>{t('demo.signedInAs', { name: 'A. Bisembin' })}</dd>
          </div>
          <div className={styles.sample}>
            <dt className={styles.sampleKey}>demo.cargo</dt>
            <dd className={styles.sampleValue}>{t('demo.cargo', { weight: formatNumber(18450), count: 3 })}</dd>
          </div>
          <div className={styles.sample}>
            <dt className={styles.sampleKey}>formatDateTime</dt>
            <dd className={styles.sampleValue}>{formatDateTime('2026-07-24T13:10:00')}</dd>
          </div>
          <div className={styles.sample}>
            <dt className={styles.sampleKey}>formatDateLong</dt>
            <dd className={styles.sampleValue}>{formatDateLong('2026-07-24T13:10:00')}</dd>
          </div>
          <div className={styles.sample}>
            <dt className={styles.sampleKey}>formatNumber</dt>
            <dd className={styles.sampleValue}>
              {formatNumber(18450)} · {formatNumber(1234567.5, 1)}
            </dd>
          </div>
          <div className={styles.sample}>
            <dt className={styles.sampleKey}>status.*</dt>
            <dd className={`${styles.sampleValue} ${styles.row}`}>
              <Badge tone="success">{t('status.onTime')}</Badge>
              <Badge tone="warning">{t('status.delayed')}</Badge>
              <Badge tone="danger">{t('status.cancelled')}</Badge>
              <Badge tone="info">{t('status.expected')}</Badge>
            </dd>
          </div>
        </dl>
      </div>
    </Section>
  </div>
  );
};
